import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

// Fixed display order for the profile switcher — matches the language
// dropdown order already used in AddBlogPost.jsx (LANGUAGE_OPTIONS).
const LANGUAGE_ORDER = ['en', 'de', 'pl', 'fr', 'es'];

const LanguageProfileContext = createContext({});

export const useLanguageProfile = () => {
  const context = useContext(LanguageProfileContext);
  if (!context) {
    throw new Error('useLanguageProfile must be used within LanguageProfileProvider');
  }
  return context;
};

const storageKey = (userId) => `gymplix_active_profile_${userId}`;

export const LanguageProfileProvider = ({ children }) => {
  const { user, userProfile } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load the user's 5 language profiles whenever the authenticated user changes.
  useEffect(() => {
    if (!user?.id || !supabase) {
      setProfiles([]);
      setActiveProfileId(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Instant paint: use the last-known active profile from this browser
    // while the real fetch resolves, so the switcher doesn't flash empty.
    try {
      const cached = localStorage.getItem(storageKey(user.id));
      if (cached) setActiveProfileId(cached);
    } catch {
      // localStorage unavailable (private mode, etc.) — non-fatal.
    }

    setLoading(true);
    supabase
      .from('language_profiles')
      .select('*')
      .eq('user_id', user.id)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error('Failed to load language profiles:', error);
          setLoading(false);
          return;
        }
        const sorted = [...(data || [])].sort(
          (a, b) => LANGUAGE_ORDER.indexOf(a.language_code) - LANGUAGE_ORDER.indexOf(b.language_code)
        );
        setProfiles(sorted);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Reconcile with the server-persisted active profile (user_profiles.active_language_profile_id,
  // already loaded by AuthContext's userProfile) once it's known — but don't
  // stomp a value the cache or the user has already set this session.
  useEffect(() => {
    if (!userProfile?.active_language_profile_id) return;
    setActiveProfileId((current) => current || userProfile.active_language_profile_id);
  }, [userProfile?.active_language_profile_id]);

  // First-ever load with no cache and no server value yet: fall back to
  // the English profile once the profiles list has actually loaded.
  useEffect(() => {
    if (loading || activeProfileId || profiles.length === 0) return;
    const fallback =
      profiles.find((p) => p.language_code === 'en')?.id || profiles[0]?.id || null;
    if (fallback) setActiveProfileId(fallback);
  }, [loading, activeProfileId, profiles]);

  const setActiveProfile = useCallback(
    async (profileId) => {
      setActiveProfileId(profileId);

      if (user?.id) {
        try {
          localStorage.setItem(storageKey(user.id), profileId);
        } catch {
          // localStorage unavailable — non-fatal, server value still persists below.
        }
      }

      if (user?.id && supabase) {
        const { error } = await supabase
          .from('user_profiles')
          .update({ active_language_profile_id: profileId })
          .eq('id', user.id);
        if (error) console.error('Failed to persist active language profile:', error);
      }
    },
    [user?.id]
  );

  const activeProfile = profiles.find((p) => p.id === activeProfileId) || null;

  const value = {
    profiles,
    activeProfile,
    activeProfileId,
    setActiveProfile,
    loading,
  };

  return (
    <LanguageProfileContext.Provider value={value}>
      {children}
    </LanguageProfileContext.Provider>
  );
};
